import React, { useState, useEffect } from "react";
import {
  Box,
  Select,
  MenuItem,
  Checkbox,
  IconButton,
  Popover,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  Tooltip,
  Button
} from "@mui/material";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { COLORS } from "../../common/colors";
import type { ComplianceProject } from "src/providers/compliance/complianceTypes";
import {
  STREAM_DISPLAY,
  STREAM_ORDER
} from "src/providers/compliance/complianceTypes";
import type {
  ComplianceFilters,
  ComplianceView,
  TriageConfig,
  TriageFilter,
  MedBasis,
  MissingDataStream
} from "src/providers/compliance/complianceFilters";
import {
  TRIAGE_FILTER_LABELS,
  MED_BASIS_LABELS,
  DATE_RANGE_OPTIONS,
  DEFAULT_FILTERS,
  DEFAULT_TD_THRESHOLD_HOURS,
  sortStreams
} from "src/providers/compliance/complianceFilters";
import { medicationName } from "src/providers/compliance/complianceMeds";
import { useCategoryMetadata } from "src/providers/CategoryMetadataContext";
import { CB, COMPLIANCE_COLORS } from "./complianceColors";

interface FilterBarProps {
  projects: ComplianceProject[];
  selectedProjectId: string | null;
  onProjectSelect: (projectId: string) => void;
  filters: ComplianceFilters;
  view: ComplianceView;
  onFiltersChange: (filters: ComplianceFilters) => void;
  totalPatients: number;
  streamMissingCounts: Record<string, number>;
}

function toInputs(tc: TriageConfig): Record<keyof TriageConfig, string> {
  return {
    missingDays: String(tc.missingDays),
    atRiskDays: String(tc.atRiskDays)
  };
}

const THRESHOLD_FIELDS: {
  label: string;
  tip: string;
  key: keyof TriageConfig;
  suffix: string;
  min: number;
  max: number;
}[] = [
  {
    label: "Out of compliance after",
    tip: "No data (or no logged dose) for this many days fails a compliance check. Exempt streams are skipped.",
    key: "missingDays",
    suffix: "days",
    min: 1,
    max: 90
  },
  {
    label: "At risk after",
    tip: "No data (or no logged dose) for this many days puts a compliance check at risk.",
    key: "atRiskDays",
    suffix: "days",
    min: 1,
    max: 90
  }
];

// Whole days, clamped to range; blank or invalid fields keep their current value.
function parseInputs(
  inputs: Record<keyof TriageConfig, string>,
  tc: TriageConfig
): TriageConfig {
  const parsed = { ...tc };
  for (const f of THRESHOLD_FIELDS) {
    const raw = Number(inputs[f.key]);
    parsed[f.key] =
      inputs[f.key] === "" || isNaN(raw)
        ? tc[f.key]
        : Math.max(f.min, Math.min(f.max, Math.round(raw)));
  }
  return parsed;
}

const CONFIG_LOCATION = "StrivePD › Admin › Compliance › Configurations";

const filterSelectSx = (active: boolean) => ({
  fontSize: 12,
  fontWeight: 500,
  fontFamily: "Work Sans, sans-serif",
  borderRadius: "999px",
  border: `1px solid ${
    active ? COLORS.PRIMARY_1000 : COMPLIANCE_COLORS.CONTROL_BORDER
  }`,
  backgroundColor: active ? COLORS.PRIMARY_200 : "#ffffff",
  color: active ? COLORS.PRIMARY_1000 : COLORS.GREY_800,
  height: 30,
  "& .MuiSelect-select": {
    px: "10px",
    py: "5px",
    pr: "28px !important",
    fontSize: 12,
    fontWeight: active ? 600 : 500
  },
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& .MuiSvgIcon-root": { fontSize: 16, right: 6, color: COLORS.GREY_500 },
  "&:hover": {
    borderColor: active ? COLORS.PRIMARY_1000 : COLORS.GREY_400,
    backgroundColor: active ? COLORS.PRIMARY_200 : COMPLIANCE_COLORS.HOVER_BG
  }
});

const menuProps = {
  PaperProps: {
    sx: {
      mt: 0.5,
      borderRadius: "8px",
      boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
      "& .MuiMenuItem-root": {
        fontSize: 12,
        fontFamily: "Work Sans, sans-serif",
        py: "6px",
        px: "12px"
      }
    }
  }
};

export function FilterBar({
  projects,
  selectedProjectId,
  onProjectSelect,
  filters,
  view,
  onFiltersChange,
  totalPatients,
  streamMissingCounts
}: FilterBarProps): JSX.Element {
  const triageActive = filters.triageFilter !== "everyone";
  const missingActive = filters.missingDataStream !== null;
  // Session overrides of the project's compliance configuration.
  const configActive =
    filters.medBasis !== null ||
    filters.triageConfig !== null ||
    filters.visibleStreams !== null;
  const tc = view.triageConfig;
  const studyMedications = view.projectConfig.studyMedications;
  const { medicationOptions } = useCategoryMetadata();

  const sortActive = filters.sortField !== "triage";
  const dateFilterActive =
    filters.createdWithinDays !== null || filters.outreachWithinDays !== null;
  // Not a project config override, so it doesn't light up the settings button; Reset clears it.
  const tdHoursActive = filters.tdThresholdHours !== null;
  const anyActive =
    triageActive ||
    missingActive ||
    configActive ||
    sortActive ||
    dateFilterActive ||
    tdHoursActive;

  const handleReset = () => onFiltersChange(DEFAULT_FILTERS);

  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(
    null
  );
  const [inputs, setInputs] = useState<Record<keyof TriageConfig, string>>(() =>
    toInputs(tc)
  );
  const setInput = (key: keyof TriageConfig, value: string) =>
    setInputs((prev) => ({ ...prev, [key]: value }));

  // Keyed on the values: `tc` is a new object on every filter change.
  const { missingDays, atRiskDays } = tc;
  useEffect(() => {
    setInputs(toInputs({ missingDays, atRiskDays }));
  }, [missingDays, atRiskDays]);

  const tdHours = filters.tdThresholdHours ?? DEFAULT_TD_THRESHOLD_HOURS;
  const [tdHoursInput, setTdHoursInput] = useState(String(tdHours));
  useEffect(() => setTdHoursInput(String(tdHours)), [tdHours]);

  const commitTdHours = () => {
    const val = Number(tdHoursInput);
    if (!Number.isInteger(val) || val < 1 || val > 24) {
      setTdHoursInput(String(tdHours));
      return;
    }
    if (val === tdHours) return;
    // Back to the default clears the override, so Reset has nothing left to undo.
    onFiltersChange({
      ...filters,
      tdThresholdHours: val === DEFAULT_TD_THRESHOLD_HOURS ? null : val
    });
  };

  const closeSettings = () => {
    setSettingsAnchor(null);
    setInputs(toInputs(tc)); // drop uncommitted or invalid edits
    setTdHoursInput(String(tdHours));
  };

  const configNote = `From this project's compliance configuration (${CONFIG_LOCATION}). Changes here only last for this session.`;

  const pending = parseInputs(inputs, tc);
  const daysConflict = pending.atRiskDays > pending.missingDays;

  // The two thresholds are validated as a pair: an out-of-order pair stays in the inputs,
  // flagged, until the other value is changed, so either can be edited first.
  const commitInputs = () => {
    if (daysConflict) return;
    if (THRESHOLD_FIELDS.every((f) => pending[f.key] === tc[f.key])) {
      setInputs(toInputs(tc));
      return;
    }
    onFiltersChange({ ...filters, triageConfig: pending });
  };

  const toggleStream = (stream: MissingDataStream) => {
    onFiltersChange({
      ...filters,
      missingDataStream: filters.missingDataStream === stream ? null : stream
    });
  };

  // Columns and compliance checks are the same list: unticking a stream hides its column and
  // stops it affecting triage.
  const toggleCheck = (stream: MissingDataStream) => {
    const current = view.columns;
    const isVisible = current.includes(stream);
    const updated = isVisible
      ? current.filter((s) => s !== stream)
      : sortStreams(Array.from(new Set([...current, stream])));
    // Clear the selected card if its column is being hidden.
    const selected = filters.missingDataStream;
    onFiltersChange({
      ...filters,
      visibleStreams: updated,
      missingDataStream:
        selected !== null && updated.includes(selected) ? selected : null
    });
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          flexWrap: "wrap",
          mt: 1.5,
          alignItems: "center"
        }}
      >
        {/* Project */}
        {selectedProjectId !== null && (
          <Select
            size="small"
            variant="outlined"
            value={selectedProjectId}
            onChange={(e) => onProjectSelect(e.target.value)}
            sx={filterSelectSx(false)}
            MenuProps={menuProps}
          >
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.displayName} ({p.patientCount})
              </MenuItem>
            ))}
          </Select>
        )}

        {/* Triage state filter */}
        <Select
          size="small"
          variant="outlined"
          value={filters.triageFilter}
          onChange={(e) =>
            onFiltersChange({
              ...filters,
              triageFilter: e.target.value as TriageFilter
            })
          }
          sx={filterSelectSx(triageActive)}
          MenuProps={menuProps}
        >
          {(Object.keys(TRIAGE_FILTER_LABELS) as TriageFilter[]).map((key) => (
            <MenuItem key={key} value={key}>
              {TRIAGE_FILTER_LABELS[key]}
            </MenuItem>
          ))}
        </Select>

        {/* Created date filter */}
        <Select
          size="small"
          variant="outlined"
          value={
            filters.createdWithinDays === null
              ? "__all__"
              : String(filters.createdWithinDays)
          }
          onChange={(e) => {
            const val =
              e.target.value === "__all__" ? null : Number(e.target.value);
            onFiltersChange({ ...filters, createdWithinDays: val });
          }}
          renderValue={(val) =>
            val === "__all__"
              ? "Created within"
              : `Created within: ${
                  DATE_RANGE_OPTIONS.find((o) => String(o.days) === val)
                    ?.label ?? val
                }`
          }
          sx={filterSelectSx(filters.createdWithinDays !== null)}
          MenuProps={menuProps}
        >
          <MenuItem value="__all__">All</MenuItem>
          {DATE_RANGE_OPTIONS.map((opt) => (
            <MenuItem key={opt.days} value={String(opt.days)}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>

        {/* Last outreach date filter */}
        <Select
          size="small"
          variant="outlined"
          value={
            filters.outreachWithinDays === null
              ? "__all__"
              : String(filters.outreachWithinDays)
          }
          onChange={(e) => {
            const val =
              e.target.value === "__all__" ? null : Number(e.target.value);
            onFiltersChange({ ...filters, outreachWithinDays: val });
          }}
          renderValue={(val) =>
            val === "__all__"
              ? "Last outreach"
              : `Last outreach: ${
                  DATE_RANGE_OPTIONS.find((o) => String(o.days) === val)
                    ?.label ?? val
                }`
          }
          sx={filterSelectSx(filters.outreachWithinDays !== null)}
          MenuProps={menuProps}
        >
          <MenuItem value="__all__">All</MenuItem>
          {DATE_RANGE_OPTIONS.map((opt) => (
            <MenuItem key={opt.days} value={String(opt.days)}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>

        <IconButton
          size="small"
          aria-label="Compliance settings"
          onClick={(e) => setSettingsAnchor(e.currentTarget)}
          sx={{
            width: 30,
            height: 30,
            border: `1px solid ${
              configActive
                ? COLORS.PRIMARY_1000
                : COMPLIANCE_COLORS.CONTROL_BORDER
            }`,
            backgroundColor: configActive ? COLORS.PRIMARY_200 : "#ffffff",
            color: configActive ? COLORS.PRIMARY_1000 : COLORS.GREY_600,
            "&:hover": {
              borderColor: configActive ? COLORS.PRIMARY_1000 : COLORS.GREY_400,
              backgroundColor: configActive
                ? COLORS.PRIMARY_200
                : COMPLIANCE_COLORS.HOVER_BG
            }
          }}
        >
          <SettingsOutlined sx={{ fontSize: 16 }} />
        </IconButton>
        {anyActive && (
          <Button
            size="small"
            onClick={handleReset}
            sx={{
              fontSize: 13,
              fontWeight: 600,
              fontFamily: "Work Sans, sans-serif",
              textTransform: "none",
              color: COLORS.PRIMARY_1500,
              minWidth: "auto",
              px: 1,
              height: 30,
              "&:hover": {
                color: COLORS.PRIMARY_1000,
                backgroundColor: "transparent"
              }
            }}
          >
            Reset
          </Button>
        )}
        <Popover
          open={Boolean(settingsAnchor)}
          anchorEl={settingsAnchor}
          onClose={closeSettings}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          PaperProps={{
            sx: {
              mt: 0.5,
              borderRadius: "8px",
              boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
              p: 2,
              minWidth: 200
            }
          }}
        >
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              fontFamily: "Work Sans, sans-serif",
              color: COLORS.GREY_700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              mb: 0.5
            }}
          >
            Compliance checks
          </Typography>
          <Typography
            sx={{
              fontSize: 11,
              fontFamily: "Work Sans, sans-serif",
              color: COLORS.GREY_600,
              mb: 0.5,
              lineHeight: 1.4
            }}
          >
            Each ticked stream is a column and a compliance check. Unticked
            streams are hidden and don&apos;t affect triage.
          </Typography>
          {(STREAM_ORDER as MissingDataStream[]).map((stream) => (
            <FormControlLabel
              key={stream}
              control={
                <Checkbox
                  size="small"
                  checked={view.columns.includes(stream)}
                  onChange={() => toggleCheck(stream)}
                  sx={{ p: "4px", "& .MuiSvgIcon-root": { fontSize: 18 } }}
                />
              }
              label={STREAM_DISPLAY[stream]?.long ?? stream}
              sx={{
                mx: 0,
                display: "flex",
                "& .MuiFormControlLabel-label": {
                  fontSize: 12,
                  fontFamily: "Work Sans, sans-serif",
                  color: COLORS.GREY_800
                }
              }}
            />
          ))}
          <Box
            sx={{
              mt: 1.5,
              borderTop: `1px solid ${COMPLIANCE_COLORS.BORDER_SECTION}`,
              pt: 1.5
            }}
          />
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 600,
              fontFamily: "Work Sans, sans-serif",
              color: COLORS.GREY_700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              mb: 0.5
            }}
          >
            Medication basis
          </Typography>
          <RadioGroup
            value={view.medBasis}
            onChange={(e) =>
              onFiltersChange({
                ...filters,
                medBasis: e.target.value as MedBasis
              })
            }
          >
            {(Object.keys(MED_BASIS_LABELS) as MedBasis[]).map((key) => (
              <FormControlLabel
                key={key}
                value={key}
                disabled={key === "study" && studyMedications.length === 0}
                control={
                  <Radio
                    size="small"
                    sx={{ p: "4px", "& .MuiSvgIcon-root": { fontSize: 16 } }}
                  />
                }
                label={MED_BASIS_LABELS[key]}
                sx={{
                  mx: 0,
                  "& .MuiFormControlLabel-label": {
                    fontSize: 12,
                    fontFamily: "Work Sans, sans-serif",
                    color: COLORS.GREY_800
                  }
                }}
              />
            ))}
          </RadioGroup>
          <Typography
            sx={{
              fontSize: 11,
              fontFamily: "Work Sans, sans-serif",
              color: COLORS.GREY_600,
              mt: 0.5,
              lineHeight: 1.4
            }}
          >
            {studyMedications.length > 0
              ? `Study meds: ${studyMedications
                  .map((c) => medicationName(c, medicationOptions))
                  .join(", ")}`
              : "No study meds configured for this project."}
          </Typography>
          <Box
            sx={{
              mt: 1.5,
              borderTop: `1px solid ${COMPLIANCE_COLORS.BORDER_SECTION}`,
              pt: 1.5
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                fontFamily: "Work Sans, sans-serif",
                color: COLORS.GREY_700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 0.5
              }}
            >
              Triage thresholds
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                fontFamily: "Work Sans, sans-serif",
                color: COLORS.GREY_600,
                mb: 1,
                lineHeight: 1.4
              }}
            >
              Each ticked stream is a compliance check, based on how recently
              data (or a logged dose) arrived. A patient is out of compliance if
              they fail any check. Medication adherence doesn&apos;t affect
              triage.
              <br />
              {configNote}
            </Typography>
            {THRESHOLD_FIELDS.map((row) => (
              <Box
                key={row.key}
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    minWidth: 260,
                    flexShrink: 0
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontFamily: "Work Sans, sans-serif",
                      color: COLORS.GREY_800
                    }}
                  >
                    {row.label}
                  </Typography>
                  <Tooltip
                    title={row.tip}
                    placement="top"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          fontSize: 11,
                          fontWeight: 400,
                          fontFamily: "Work Sans, sans-serif",
                          maxWidth: 220
                        }
                      }
                    }}
                  >
                    <InfoOutlined
                      sx={{
                        fontSize: 14,
                        color: COLORS.GREY_500,
                        cursor: "help"
                      }}
                    />
                  </Tooltip>
                </Box>
                <TextField
                  type="number"
                  size="small"
                  value={inputs[row.key]}
                  onChange={(e) => setInput(row.key, e.target.value)}
                  onBlur={commitInputs}
                  onKeyDown={(e) => {
                    if (e.key === "Enter")
                      (e.target as HTMLInputElement).blur();
                  }}
                  inputProps={{
                    min: row.min,
                    max: row.max,
                    style: {
                      fontSize: 12,
                      fontFamily: "Work Sans, sans-serif",
                      width: 36,
                      padding: "4px 8px"
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "6px",
                      ...(daysConflict
                        ? {
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: CB.VERMILLION
                            }
                          }
                        : {})
                    },
                    "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                      { WebkitAppearance: "none", margin: 0 },
                    "& input[type=number]": { MozAppearance: "textfield" }
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 12,
                    fontFamily: "Work Sans, sans-serif",
                    color: COLORS.GREY_700
                  }}
                >
                  {row.suffix}
                </Typography>
              </Box>
            ))}
            {daysConflict && (
              <Typography
                role="alert"
                sx={{
                  fontSize: 11,
                  fontFamily: "Work Sans, sans-serif",
                  color: CB.VERMILLION_TEXT,
                  lineHeight: 1.4,
                  maxWidth: 340
                }}
              >
                &quot;At risk after&quot; must be at or below &quot;Out of
                compliance after&quot;. Change either value to fix.
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              mt: 1.5,
              borderTop: `1px solid ${COMPLIANCE_COLORS.BORDER_SECTION}`,
              pt: 1.5
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                fontFamily: "Work Sans, sans-serif",
                color: COLORS.GREY_700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                mb: 0.5
              }}
            >
              Patient sidebar
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                fontFamily: "Work Sans, sans-serif",
                color: COLORS.GREY_600,
                mb: 1,
                lineHeight: 1.4
              }}
            >
              Display only: doesn&apos;t affect triage. Applies to this project
              until you reload the page.
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                sx={{
                  fontSize: 12,
                  fontFamily: "Work Sans, sans-serif",
                  color: COLORS.GREY_800,
                  minWidth: 260
                }}
              >
                Tremor &amp; dyskinesia daily hours line
              </Typography>
              <TextField
                type="number"
                size="small"
                value={tdHoursInput}
                onChange={(e) => setTdHoursInput(e.target.value)}
                onBlur={commitTdHours}
                onKeyDown={(e) => {
                  if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                }}
                inputProps={{
                  min: 1,
                  max: 24,
                  "aria-label": "Tremor and dyskinesia daily hours line",
                  style: {
                    fontSize: 12,
                    fontFamily: "Work Sans, sans-serif",
                    width: 36,
                    padding: "4px 8px"
                  }
                }}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "6px" },
                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                    { WebkitAppearance: "none", margin: 0 },
                  "& input[type=number]": { MozAppearance: "textfield" }
                }}
              />
              <Typography
                sx={{
                  fontSize: 12,
                  fontFamily: "Work Sans, sans-serif",
                  color: COLORS.GREY_700
                }}
              >
                hours
              </Typography>
            </Box>
          </Box>
        </Popover>
      </Box>

      {/* Data filter cards */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", mt: 1.5 }}>
        <Box
          role="button"
          tabIndex={0}
          aria-pressed={!missingActive}
          onClick={() =>
            onFiltersChange({ ...filters, missingDataStream: null })
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              onFiltersChange({ ...filters, missingDataStream: null });
            }
          }}
          sx={{
            flex: "1 1 150px",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 2,
            py: 1.5,
            borderRadius: "10px",
            cursor: "pointer",
            backgroundColor: !missingActive ? COLORS.PRIMARY_1000 : "#ffffff",
            color: !missingActive ? "#ffffff" : COLORS.PRIMARY_1000,
            border: `1px solid ${
              !missingActive
                ? COLORS.PRIMARY_1000
                : COMPLIANCE_COLORS.BORDER_SECTION
            }`,
            transition: "all 0.15s ease",
            "&:hover": {
              backgroundColor: !missingActive
                ? COLORS.PRIMARY_1000
                : COMPLIANCE_COLORS.HOVER_BG
            }
          }}
        >
          <Typography
            sx={{
              fontSize: 24,
              fontWeight: 600,
              fontFamily: "Work Sans, sans-serif",
              lineHeight: 1
            }}
          >
            {totalPatients}
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 500,
              fontFamily: "Work Sans, sans-serif",
              lineHeight: 1.3
            }}
          >
            Total Patients
          </Typography>
        </Box>
        {view.columns.map((key) => {
          const active = filters.missingDataStream === key;
          const count = streamMissingCounts[key] ?? 0;
          const label = STREAM_DISPLAY[key]?.short ?? key;
          return (
            <Box
              key={key}
              role="button"
              tabIndex={0}
              aria-pressed={active}
              onClick={() => toggleStream(key)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleStream(key);
                }
              }}
              sx={{
                flex: "1 1 150px",
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1.5,
                borderRadius: "10px",
                cursor: "pointer",
                backgroundColor: active ? COLORS.PRIMARY_1000 : "#ffffff",
                border: `1px solid ${
                  active
                    ? COLORS.PRIMARY_1000
                    : COMPLIANCE_COLORS.BORDER_SECTION
                }`,
                transition: "all 0.15s ease",
                "&:hover": {
                  backgroundColor: active
                    ? COLORS.PRIMARY_1000
                    : COMPLIANCE_COLORS.HOVER_BG
                }
              }}
            >
              <Typography
                sx={{
                  fontSize: 24,
                  fontWeight: 600,
                  fontFamily: "Work Sans, sans-serif",
                  color: active ? "#ffffff" : COLORS.PRIMARY_1000,
                  lineHeight: 1
                }}
              >
                {count}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 500,
                  fontFamily: "Work Sans, sans-serif",
                  color: active ? "#ffffff" : COLORS.GREY_800,
                  lineHeight: 1.3
                }}
              >
                No {label}
                <br />
                for {tc.missingDays} days
              </Typography>
            </Box>
          );
        })}
      </Box>
    </>
  );
}
